import { z } from 'zod';
import { BaseEntitySchema, TimestampSchema } from './common';
import { GameConfigSchema } from './game-config';
import {
  GameDefinitionId,
  ImageId,
  InviteTokenId,
  PlayerId,
  SessionId,
  SpectatorId,
  TeamId,
  UserId,
} from './ids';
import { PlayerSchema } from './player';
import { RoundSchema } from './round';
import { TeamSchema } from './team';

export const SessionStatusSchema = z.enum(['ongoing', 'paused', 'finished']);
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

export const PlayerOrTeamIdSchema = z.union([PlayerId, TeamId]);
export type PlayerOrTeamId = z.infer<typeof PlayerOrTeamIdSchema>;

export const SessionSchema = BaseEntitySchema.extend({
  id: SessionId,
  name: z.string().min(1).max(200).optional(),
  notes: z.string().max(2000).optional(),
  tags: z.array(z.string().min(1).max(50)).optional(),
  gameConfigSnapshot: GameConfigSchema, // full config snapshot at session start
  players: z.array(PlayerSchema),
  teams: z.array(TeamSchema).optional(),
  rounds: z.array(RoundSchema),
  imageIds: z.array(ImageId).default([]),
  spectators: z
    .array(
      z.object({
        id: SpectatorId,
        userId: UserId.optional(),
        joinedAt: TimestampSchema,
      }),
    )
    .default([]),
  startedAt: TimestampSchema,
  pausedAt: TimestampSchema.optional(), // latest pause timestamp
  endedAt: TimestampSchema.optional(),
  archivedAt: TimestampSchema.optional(),
  // Derived (computed from events)
  status: SessionStatusSchema,
  currentRoundNumber: z.number().int().nonnegative(), // derived from rounds
  winner: PlayerOrTeamIdSchema.optional(), // playerId or teamId
  // Session visibility settings
  isPublicSpectator: z.boolean().default(false), // Anyone can view as spectator without invite token
  isPublicPlayerJoin: z.boolean().default(false), // Anyone can join as player without approval
});
export type Session = z.infer<typeof SessionSchema>;

export const SessionMetadataSchema = z.object({
  id: SessionId,
  name: z.string().min(1).max(200).optional(),
  playerCount: z.number().int().nonnegative(),
  status: SessionStatusSchema,
  startedAt: TimestampSchema,
  endedAt: TimestampSchema.optional(),
  pausedAt: TimestampSchema.optional(),
  deletedAt: TimestampSchema.optional(),
  archivedAt: TimestampSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  winner: PlayerOrTeamIdSchema.optional(),
  currentRoundNumber: z.number().int().nonnegative(),
  userId: UserId.optional(), // Optional: sessions can be created by guests before authentication
  gameDefinitionId: GameDefinitionId.optional(), // Optional: link to game definition if session was created from one
  inviteToken: InviteTokenId.nullable().optional(), // Invite token for session access control
  // Session visibility settings
  isPublicSpectator: z.boolean().default(false), // Anyone can view as spectator without invite token
  isPublicPlayerJoin: z.boolean().default(false), // Anyone can join as player without approval
});
export type SessionMetadata = z.infer<typeof SessionMetadataSchema>;
